import React, { useCallback, useEffect, useMemo } from "react";
import HeaderFilterMenus, {
  FilterMenuConfig,
} from "@/layout-components/HeaderFilterMenus";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { useWidgetFilterState } from "@/hooks/useWidgetFilterState";
import { SearchFiltersFooterProps } from "@/widget-components/newsquawk-headlines/SearchFiltersFooter";

export function useHeaderFilterMenu(
  filterConfig: FilterMenuConfig[],
  pushAction?: (action: React.ReactElement) => void,
  persistKey?: string,
  icon?: React.ReactElement,
  footer?: React.ReactElement<SearchFiltersFooterProps>
) {
  const [selections, setSelections] = useWidgetFilterState(persistKey, "filter");

  const clearSelections = useCallback(() => {
    setSelections({});
  }, [setSelections]);

  /**
   * Creates a memoized footer element by cloning the provided footer component with additional props.
   *
   *
   * @remarks
   * The footer element is enhanced with:
   * - `clearSelections` function to reset filter selections
   * - `disableClear` boolean that is true when there are no active selections
   *
   * This component is memoized and only re-renders when footer component, clearSelections function,
   * or selections object changes.
   */
  const _footer = useMemo(() => {
    if (!footer) return null;

    return React.cloneElement(footer, {
      clearSelections,
      disableClear: !Object.keys(selections).length,
    });
  }, [footer, clearSelections, selections]);

  useEffect(() => {
    const Filters = (
      <HeaderFilterMenus
        key={persistKey}
        label="Filter"
        filterMenus={filterConfig}
        selections={selections}
        onChange={setSelections}
        icon={icon || <SparklesIcon className="size-4" />}
        persistKey={persistKey}
        footer={_footer}
      />
    );

    if (!pushAction) return;
    pushAction(Filters);
  }, [
    pushAction,
    filterConfig,
    persistKey,
    selections,
    setSelections,
    icon,
    _footer,
  ]);

  return [selections, setSelections] as const;
}
